/**
 * Documento imprimible de la Declaración de Renta AT 2026 del EMPRESARIO INDIVIDUAL
 * Régimen Pro PYME (Art. 14 letra D) N° 3 LIR) — Recuadros N° 17 a 21, Impuesto de Primera
 * Categoría de la empresa, Impuesto Global Complementario del titular y liquidación única.
 *
 * El membrete, los antecedentes, los estilos, la nota legal y las firmas son los de
 * todos los PDF de renta (F22/common/pdfComun.js); aquí van los recuadros de la empresa,
 * las secciones de la persona (persona/pdfPersona.js), la liquidación y las notas.
 */
const { fmt, fieldRow, subsectionHtml, sectionHtml, buildDocumento, abrirParaImprimir } = require('../../../F22/common/pdfComun');
const { r17: r17Fn, r18: r18Fn, r19: r19Fn, r20: r20Fn, r21: r21Fn } = require('./formulas');
const { calcularLiquidacion } = require('./liquidacion');
const { buildBaseImponibleIGC, buildImpuestoGlobalComplementario } = require('../persona/pdfPersona');

const ANIO_TRIBUTARIO = 2026;
const ANIO_COMERCIAL = ANIO_TRIBUTARIO - 1;

// -----------------------------------------------------------------------
// R17 – Base Imponible (ingresos y egresos)
// -----------------------------------------------------------------------
const buildR17 = (data, computed) => {
  const ingresos = subsectionHtml('Ingresos', [
    fieldRow('(+)', '1400', 'Ingresos del giro percibidos',                           fmt(r17Fn.ingresosGiroPercibidos(data))),
    fieldRow('(+)', '1817', 'Ingresos devengados en ejercicios anteriores y percibidos', fmt(r17Fn.ingresosDevengadosPercibidos(data))),
    fieldRow('(+)', '1401', 'Rentas de fuente extranjera percibidas',                  fmt(r17Fn.rentasFuenteExtranjera(data))),
    fieldRow('(+)', '1587', 'Ingresos operaciones con empresas relacionadas (14A)',    fmt(r17Fn.ingresosRelacionados14A(data))),
    fieldRow('(+)', '1588', 'Otros ingresos percibidos o devengados',                  fmt(r17Fn.otrosIngresosPercibidos(data))),
    fieldRow('(=)', '1410', 'Total de ingresos anuales',                               fmt(computed.r17?.totalIngresos), 'subtotal'),
  ]);

  const egresos = subsectionHtml('Egresos', [
    fieldRow('(-)', '1409', 'Existencias, insumos y servicios pagados',                fmt(r17Fn.existenciasPagadas(data))),
    fieldRow('(-)', '1818', 'Gastos adeudados en ejercicios anteriores y pagados',     fmt(r17Fn.gastosAdeudadosPagados(data))),
    fieldRow('(-)', '1411', 'Remuneraciones pagadas',                                  fmt(r17Fn.remuneracionesPagadas(data))),
    fieldRow('(-)', '1412', 'Honorarios pagados',                                      fmt(r17Fn.honorariosPagados(data))),
    fieldRow('(-)', '1413', 'Adquisición bienes activo fijo, pagados',                 fmt(r17Fn.activoFijoPagado(data))),
    fieldRow('(-)', '1415', 'Arriendos pagados',                                       fmt(r17Fn.arriendosPagados(data))),
    fieldRow('(-)', '1419', 'Intereses y reajustes pagados',                           fmt(r17Fn.interesesPagados(data))),
    fieldRow('(-)', '1422', 'Partidas art. 21 inc. 1° y 2° LIR pagados',              fmt(r17Fn.partidasArt21(data))),
    fieldRow('(-)', '1424', 'Otros gastos deducibles',                                 fmt(r17Fn.otrosGastos(data))),
    fieldRow('(=)', '1430', 'Total de egresos anuales',                                fmt(computed.r17?.totalEgresos), 'subtotal'),
  ]);

  return sectionHtml(
    'I',
    'Recuadro N° 17 — Base Imponible',
    'Régimen Pro PYME, Art. 14 letra D) N° 3 de la Ley sobre Impuesto a la Renta',
    ingresos + egresos
  );
};

// -----------------------------------------------------------------------
// R18 – Determinación del RAI
// -----------------------------------------------------------------------
const buildR18 = (data, computed) => {
  const rows = [
    fieldRow('(+)', '1703', 'CPTS positivo final (recuadro N° 19)',                   fmt(r18Fn.cptsPositivo(data))),
    fieldRow('(+)', '1704', 'Remesas, retiros o dividendos repartidos, históricos',    fmt(r18Fn.retirosHistoricos(data))),
    fieldRow('(=)', '1720', 'Subtotal',                                                fmt(r18Fn.subtotal(data)), 'subtotal'),
    fieldRow('(-)', '1493', 'Saldo positivo del registro REX antes de imputaciones',  fmt(r18Fn.saldoREXPositivo(data))),
    fieldRow('(-)', '1494', 'Capital aportado, histórico',                             fmt(r18Fn.capitalAportado(data))),
    fieldRow('(=)', '1500', 'Rentas afectas a IGC o IA (RAI) del ejercicio',           fmt(computed.r18?.rai), 'total'),
  ];
  return sectionHtml(
    'II',
    'Recuadro N° 18 — Determinación del RAI',
    'Rentas afectas a Impuesto Global Complementario o Adicional',
    `<div class="subsection">${rows.join('')}</div>`
  );
};

// -----------------------------------------------------------------------
// R19 – CPTS
// -----------------------------------------------------------------------
const buildR19 = (data, computed) => {
  const rows = [
    fieldRow('(+)', '1445', 'CPT o CPTS positivo inicial',                            fmt(r19Fn.cptInicial(data))),
    fieldRow('(+)', '1374', 'Capital aportado empresas que inician actividades en el año comercial', fmt(r19Fn.capitalAportado1374(data))),
    fieldRow('(+)', '1705', 'Base imponible afecta a IDPC del ejercicio',              fmt(r19Fn.biAfecta(data))),
    fieldRow('(+)', '1726', 'Retiros o dividendos percibidos en el ejercicio',         fmt(r19Fn.dividendosPercibidos(data))),
    fieldRow('(-)', '1479', 'Remesas, retiros o dividendos repartidos',                fmt(r19Fn.retiros(data))),
    fieldRow('(-)', '1708', 'Partidas inc. 1° art. 21 LIR',                           fmt(r19Fn.partidasArt21(data))),
    fieldRow('(-)', '1379', 'Crédito IPE contra IF del ejercicio',                    fmt(r19Fn.creditoIPE(data))),
    fieldRow('(+)', '1710', 'Incentivo al ahorro art. 14 letra E)',                   fmt(r19Fn.incentivoAhorro(data))),
    fieldRow('(=)', '1545', 'CPTS positivo final',                                    fmt(computed.r19?.cptsPositivoFinal), 'total'),
  ];
  return sectionHtml(
    'III',
    'Recuadro N° 19 — Capital Propio Tributario Simplificado',
    'Determinación del CPTS al término del ejercicio',
    `<div class="subsection">${rows.join('')}</div>`
  );
};

// -----------------------------------------------------------------------
// R20 – Registro RTRE (tabla RAI / INR / STUT)
// -----------------------------------------------------------------------
const buildR20 = (data) => {
  const rem    = r20Fn.remanente(data);
  const remSig = r20Fn.remanenteSiguiente(data);

  const tableRows = [
    { label: 'Remanente ejercicio anterior (saldo positivo)', code: '1451/1454/1382', values: rem },
    { label: 'Reversos y/o disminuciones (propios)',          code: '1459',           values: { RAI: r20Fn.reverso(data).RAI } },
    { label: 'Aumentos del ejercicio (propios)',              code: '1463',           values: { RAI: r20Fn.aumentoRAI(data).RAI } },
    { label: 'Otros aumentos del ejercicio',                  code: '1470/1387',      values: { REX_INR: r20Fn.aumentoINR(data).REX_INR } },
    { label: 'Retiros imputados a RTRE',                      code: '1475',           values: { RAI: r20Fn.retiros(data) } },
    { label: 'Remanente ejercicio siguiente',                 code: '1484/1487/1391', values: remSig, isTotal: true },
  ];

  const bodyRows = tableRows.map((r) => {
    const cls = r.isTotal ? ' class="row-total"' : '';
    return `<tr${cls}>
      <td class="td-label">${r.label}</td>
      <td class="td-code">${r.code}</td>
      <td>${fmt(r.values.RAI)}</td>
      <td>${fmt(r.values.REX_INR)}</td>
      <td>${fmt(r.values.STUT)}</td>
    </tr>`;
  }).join('');

  const content = `
    <table class="data-table">
      <thead>
        <tr>
          <th class="td-label">Detalle</th>
          <th class="td-code">Código</th>
          <th>RAI</th>
          <th>INR</th>
          <th>STUT</th>
        </tr>
      </thead>
      <tbody>${bodyRows}</tbody>
    </table>`;

  return sectionHtml(
    'IV',
    'Recuadro N° 20 — Registro RTRE y movimiento STUT',
    'Registro Tributario de Rentas Empresariales',
    content
  );
};

// -----------------------------------------------------------------------
// R21 – Registro SAC
// -----------------------------------------------------------------------
const buildR21 = (data) => {
  const rem        = r21Fn.remanente(data);
  const idpcBI     = r21Fn.idpcBI(data);
  const idpcDiv    = r21Fn.idpcDividendo(data);
  const asigRet    = r21Fn.asignadoRetiros(data);
  const gastosR    = r21Fn.gastosRechazados(data);
  const remSig     = r21Fn.remanenteSiguiente(data);

  const sacRows = [
    { label: 'Remanente ejercicio anterior',      noSujetoSinDev: rem.SAC_noSujetoSinDev,    noSujetoConDev: rem.SAC_noSujetoConDev,    sujetoConDev: rem.SAC_sujetoConDev,    ipeSinDev: rem.SAC_IPE_sinDev,       sac2016: rem.SAC_2016_conDev },
    { label: 'IDPC e IPE base imponible',          noSujetoSinDev: idpcBI.SAC_IPE_sinDev,     noSujetoConDev: idpcBI.SAC_noSujetoConDev, sujetoConDev: 0,                       ipeSinDev: idpcBI.SAC_IPE_IF,        sac2016: 0 },
    { label: 'IDPC e IPE dividendos percibidos',  noSujetoSinDev: 0,                          noSujetoConDev: idpcDiv.SAC_noSujetoConDev, sujetoConDev: idpcDiv.SAC_sujetoConDev, ipeSinDev: 0,                       sac2016: idpcDiv.SAC_2016_conDev },
    { label: 'Asignado a retiros',                noSujetoSinDev: asigRet.SAC_noSujetoSinDev, noSujetoConDev: asigRet.SAC_noSujetoConDev, sujetoConDev: 0,                      ipeSinDev: asigRet.SAC_IPE,          sac2016: 0 },
    { label: 'Asignado a gastos rechazados',      noSujetoSinDev: 0,                          noSujetoConDev: gastosR.SAC_noSujetoConDev, sujetoConDev: 0,                       ipeSinDev: 0,                        sac2016: 0 },
    { label: 'Remanente ejercicio siguiente',     noSujetoSinDev: remSig.SAC_noSujetoSinDev,  noSujetoConDev: remSig.SAC_noSujetoConDev, sujetoConDev: remSig.SAC_sujetoConDev, ipeSinDev: remSig.SAC_IPE_sinDev,   sac2016: remSig.SAC_2016_conDev, isTotal: true },
  ];

  const bodyRows = sacRows.map((r) => {
    const cls = r.isTotal ? ' class="row-total"' : '';
    return `<tr${cls}>
      <td class="td-label">${r.label}</td>
      <td>${fmt(r.noSujetoSinDev)}</td>
      <td>${fmt(r.noSujetoConDev)}</td>
      <td>${fmt(r.sujetoConDev)}</td>
      <td>${fmt(r.ipeSinDev)}</td>
      <td>${fmt(r.sac2016)}</td>
    </tr>`;
  }).join('');

  const content = `
    <table class="data-table">
      <thead>
        <tr>
          <th class="td-label">Detalle</th>
          <th>No Suj. S/Dev</th>
          <th>No Suj. C/Dev</th>
          <th>Suj. C/Dev</th>
          <th>IPE</th>
          <th>Cred. 2016</th>
        </tr>
      </thead>
      <tbody>${bodyRows}</tbody>
    </table>`;

  return sectionHtml(
    'V',
    'Recuadro N° 21 — Registro SAC',
    'Saldo Acumulado de Créditos contra impuestos finales',
    content
  );
};

// -----------------------------------------------------------------------
// Impuesto de Primera Categoría de la empresa (línea 54). Termina en el IDPC:
// los PPM y el resultado del F22 van en la liquidación única, junto con el
// impuesto del titular.
// -----------------------------------------------------------------------
const buildImpuestoPrimeraCategoria = (data, computed) => {
  const tasaIDPC = data.antecedentes?.tasaIDPC || 0;
  const liq = calcularLiquidacion(data);
  const rows = [
    fieldRow('(+)', '1431', 'Partidas inc. 1° art. 21 LIR (gastos rechazados)',        fmt(r17Fn.gastosRechazadosArt21(data))),
    fieldRow('(=)', '1729', 'Base Imponible antes de incentivo al ahorro',             fmt(computed.r17?.biAntesIncentivo), 'subtotal'),
    fieldRow('(-)', '1432', 'Incentivo al ahorro art. 14 letra E)',                    fmt(r17Fn.incentivoAhorro(data))),
    fieldRow('(=)', '1440', 'Base Imponible afecta a IDPC',                            fmt(computed.r17?.biAfectaIDPC), 'total'),
    fieldRow('',    '18',   'Línea 54 · Base imponible',                               fmt(liq[18])),
    fieldRow('(-)', '19',   'Línea 54 · Rebajas al impuesto',                          fmt(liq[19])),
    fieldRow('(=)', '20',   `Línea 54 · Impuesto de Primera Categoría (tasa ${tasaIDPC * 100}%)`, fmt(liq[20]), 'total'),
  ];
  const exencion = liq.exentoIDPC
    ? `<div class="nota-item"><p>Exento del Impuesto de Primera Categoría: la renta líquida no supera 1 UTA de diciembre
      de ${ANIO_COMERCIAL} ($834.504), conforme al artículo 40 N° 6 de la Ley sobre Impuesto a la Renta.</p></div>`
    : '';

  return sectionHtml(
    'VI',
    'Impuesto de Primera Categoría',
    `Base imponible e impuesto de la empresa individual — año comercial ${ANIO_COMERCIAL}`,
    `<div class="subsection">${rows.join('')}</div>${exencion}`
  );
};

// -----------------------------------------------------------------------
// Liquidación única del F22: cierre del documento. Junta el impuesto del
// titular y el de su empresa, menos los PPM, la mayor retención y los
// remanentes devolvibles. Se destaca con doble línea (convención contable).
// -----------------------------------------------------------------------
const buildLiquidacion = (data) => {
  const liq = calcularLiquidacion(data);
  const impuestos = subsectionHtml('Impuestos', [
    fieldRow('(+)', '31', 'Línea 53 · Impuesto Global Complementario del titular', fmt(liq[31])),
    fieldRow('(+)', '20', 'Línea 54 · Impuesto de Primera Categoría de la empresa', fmt(liq[20])),
  ]);
  const deducciones = subsectionHtml('Deducciones', [
    fieldRow('(-)', '36',  'Línea 85 · Pagos provisionales mensuales actualizados', fmt(liq[36])),
    fieldRow('(-)', '54',  'Línea 92 · Mayor retención de impuesto único solicitada', fmt(liq[54])),
    fieldRow('(-)', '757', 'Línea 97 · Remanentes de crédito devolvibles', fmt(liq[757])),
  ]);
  const empresa = subsectionHtml('Resultado de la renta de la empresa', [
    fieldRow('(=)', '', 'Impuesto de Primera Categoría (20) menos pagos provisionales (36)', fmt(liq.resultadoEmpresa), 'total'),
  ]);
  const resultado = subsectionHtml('Resultado del Formulario 22 (empresa y titular)', [
    fieldRow('(=)', '305', 'RESULTADO LIQUIDACIÓN ANUAL IMPUESTO A LA RENTA', fmt(liq[305]), 'tax-result'),
    fieldRow('', '85', 'Línea 110 · Saldo a favor', fmt(liq[85])),
    fieldRow('', '87', 'Línea 112 · Devolución solicitada', fmt(liq[87])),
    fieldRow('', '90', 'Línea 113 · Impuesto adeudado', fmt(liq[90])),
  ]);

  return sectionHtml(
    'IX',
    'Liquidación del Formulario 22',
    'Impuesto del titular y de su empresa individual en una sola declaración',
    `${impuestos}${deducciones}${empresa}${resultado}`,
    'section-final'
  );
};

// -----------------------------------------------------------------------
// Notas relevantes para la presentación de abril. Se redactan en forma
// condicional porque el saldo definitivo (a pagar o a favor) solo queda
// determinado al presentar la declaración: este documento llega hasta el
// impuesto determinado, sin imputar el reajuste del art. 72 LIR.
// -----------------------------------------------------------------------
const buildNotasDeclaracion = () => `
  <div class="notas-block">
    <div class="block-title">Notas relevantes para la presentación del Formulario 22</div>

    <div class="nota-item">
      <div class="nota-title">1. Reajuste del artículo 72 de la Ley sobre Impuesto a la Renta (código 39)</div>
      <p>Si de la determinación contenida en este documento resulta impuesto a pagar, el monto que deberá enterarse
      efectivamente en arcas fiscales al momento de presentar el Formulario 22 será superior al aquí determinado. Lo
      anterior, por aplicación del artículo 72 de la Ley sobre Impuesto a la Renta, que dispone el reajuste del saldo
      de impuesto adeudado conforme a la variación experimentada por el Índice de Precios al Consumidor entre el último
      día del mes anterior al cierre del ejercicio y el último día del mes anterior a aquel en que legalmente deba
      pagarse el tributo. Dicho reajuste se declara en el código 39 del Formulario 22, se calcula exclusivamente sobre
      el impuesto adeudado (código 90) y su porcentaje lo publica el Servicio de Impuestos Internos durante el mes de
      abril.</p>
      <p>Este reajuste no resulta aplicable cuando la determinación arroja impuesto igual a cero, pérdida tributaria o
      un saldo a favor del contribuyente. En tales casos, las cifras contenidas en el presente documento no
      experimentarán aumento alguno por este concepto.</p>
    </div>

    <div class="nota-item">
      <div class="nota-title">2. Devolución de pagos provisionales mensuales y saldo a favor del contribuyente</div>
      <p>En caso de que los pagos provisionales mensuales enterados durante el ejercicio, los remanentes de créditos
      devolvibles del titular y demás créditos imputables excedan los impuestos determinados, se generará un saldo a
      favor del contribuyente cuya devolución debe solicitarse en la misma declaración anual de impuestos a la renta.</p>
      <p>Para que la Tesorería General de la República pueda materializar dicha devolución mediante depósito, el
      contribuyente deberá informar oportunamente a su contador los datos de la cuenta bancaria en que se efectuará el
      abono —de titularidad del empresario individual, que declara con su propio RUT—, precisando institución
      bancaria, tipo de cuenta y número de cuenta. Dicha información deberá ser proporcionada con anterioridad a la
      presentación de la declaración.</p>
      <p>La devolución es depositada por la Tesorería General de la República con posterioridad al mes de abril, en las
      fechas que dicho organismo determine según la modalidad y oportunidad de presentación de la declaración. La
      omisión, inexactitud o desactualización de los datos bancarios podrá impedir o retrasar el pago de la devolución.</p>
    </div>

    <div class="nota-item">
      <div class="nota-title">3. Rentas del titular</div>
      <p>Las rentas y créditos del titular (sueldos, intereses, retiros, rentas exentas y crédito por gastos en
      educación) provienen de la información de terceros del Servicio de Impuestos Internos cargada y validada para
      este año tributario. Las rebajas por cotizaciones previsionales del titular, intereses por créditos con garantía
      hipotecaria y ahorro previsional voluntario (líneas 20 a 23) no se encuentran incluidas en esta determinación.</p>
    </div>
  </div>`;

// -----------------------------------------------------------------------
// Documento completo
// -----------------------------------------------------------------------
const buildR17_R21_Html = (data, computed, company = {}) => {
  const sectionsHtml = [
    buildR17(data, computed),
    buildR18(data, computed),
    buildR19(data, computed),
    buildR20(data),
    buildR21(data),
    buildImpuestoPrimeraCategoria(data, computed),
    buildBaseImponibleIGC(data, 'VII'),
    buildImpuestoGlobalComplementario(data, 'VIII'),
    buildLiquidacion(data),
  ].join('');

  return buildDocumento(company, {
    anioTributario: ANIO_TRIBUTARIO,
    anioComercial: ANIO_COMERCIAL,
    regimen: 'Pro PYME General — Art. 14 D) N° 3 LIR · empresario individual',
    formulario: 'N° 22 — Recuadros N° 17 a 21 y Global Complementario',
    subtitulos: [
      'Empresario individual: Impuesto de Primera Categoría de la empresa e Impuesto Global Complementario del titular en una sola declaración',
      'Régimen Pro PYME — Art. 14 letra D) N° 3 de la Ley sobre Impuesto a la Renta',
    ],
    secciones: sectionsHtml,
    notas: buildNotasDeclaracion(),
    notaLegal: {
      queSeDetermina: 'La determinación de la base imponible y del Impuesto de Primera Categoría de la empresa individual, y del Impuesto Global Complementario de su titular,',
      articulo: 'artículo 14 letra D) N° 3',
    },
  });
};

// Exportación vía impresión del navegador (se mantiene por compatibilidad)
const downloadR17_R21_Pdf = (data, computed, company) => abrirParaImprimir(buildR17_R21_Html(data, computed, company));

module.exports = { buildR17_R21_Html, downloadR17_R21_Pdf };
